import React from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import founderImage from '../../../../public/assets/Founderquote.png';

const FounderSection = () => {
  const founderData = {
    name: "Mr. Abhishek Aggarwal",
    title: "Founder & CEO",
    image: founderImage,
    imageAlt: "Professional portrait of confident woman with shoulder-length brown hair wearing navy blazer and white shirt in modern office setting with natural lighting",
    quote: "Technology should empower businesses, not complicate them. Our mission is to bridge the gap between complex technical possibilities and clear business outcomes.",
    story: `Founded in 2021, Devontis Technologies emerged from a simple observation: businesses needed more than just developers—they needed strategic technology partners who understood both code and commerce.\n\nWith over 15 years of experience leading engineering teams at Fortune 500 companies and successful startups, I witnessed firsthand the disconnect between technical execution and business strategy. Too often, brilliant technical solutions failed to deliver business value because they weren't aligned with organizational goals.\n\nDevontis was born from the conviction that technology partnerships should be transformative, not transactional. We built a team of craftspeople who are as comfortable discussing quarterly revenue targets as they are architecting microservices. Every project we undertake is guided by one question: How does this drive measurable business outcomes?\n\nToday, we're proud to serve clients across logistics, healthcare, fintech, and manufacturing—industries where the stakes are high and the margin for error is slim. Our success is measured not in lines of code, but in the transformations we enable for our partners.`
  };

  const milestones = [
  {
    year: "2021",
    title: "Foundation",
    description: "Devontis Technologies established with vision to transform SaaS development partnerships",
    icon: "Rocket"
  },
  {
    year: "2022",
    title: "Education Platform Launch",
    description: "Partnered with an institutional client to design and deliver a scalable digital learning platform for students",
    icon: "Building2"
  },
  {
    year: "2023",
    title: "Team Expansion",
    description: "Grew to 10+ team members across development, design, and strategic consulting",
    icon: "Users"
  },
  {
    year: "2024",
    title: "Industry Validation",
    description: "Recognized for delivering reliable, scalable software solutions across regulated and growth-focused teams",
    icon: "Award"
  },
  {
    year: "2025",
    title: "International Presence",
    description: "Supporting organizations across multiple regions with distributed delivery and consistent execution standards",
    icon: "Globe"
  }
  ];


  return (
    <section className="py-16 md:py-24 lg:py-32 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 lg:gap-16 items-center mb-16 md:mb-24">
          <div className="order-2 lg:order-1">
            <div className="inline-flex items-center space-x-2 bg-primary/10 px-4 py-2 rounded-full mb-4 md:mb-6">
              <Icon name="Heart" size={18} color="var(--color-primary)" />
              <span className="text-sm font-medium font-body text-primary">Founder's Journey</span>
            </div>
            
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold font-headline text-foreground mb-4 md:mb-6">
              Building Technology Partnerships That Matter
            </h2>
            
            <div className="bg-primary/5 border-l-4 border-primary px-4 md:px-6 py-4 rounded-r-lg mb-6 md:mb-8">
              <p className="text-base md:text-lg text-foreground font-body italic leading-relaxed">
                "{founderData?.quote}"
              </p>
              <div className="mt-4 flex items-center space-x-3">
                <div>
                  <p className="font-semibold font-headline text-foreground">{founderData?.name}</p>
                  <p className="text-sm text-muted-foreground font-body">{founderData?.title}</p>
                </div>
              </div>
            </div>
            
            <div className="space-y-4 text-muted-foreground font-body text-sm md:text-base leading-relaxed">
              {founderData?.story?.split('\n\n')?.map((paragraph, index) =>
              <p key={index}>{paragraph}</p>
              )}
            </div>
          </div>
          
          <div className="order-1 lg:order-2">
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-br from-primary/20 to-accent/20 rounded-2xl blur-2xl"></div>
              <div className="relative aspect-[3/4] rounded-2xl overflow-hidden shadow-2xl">
                <Image
                  src={founderData?.image}
                  alt={founderData?.imageAlt}
                  className="w-full h-full object-cover" />

              </div>
              
              <div className="absolute -bottom-6 -right-6 bg-card p-4 md:p-6 rounded-xl shadow-lg border border-border">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                    <Icon name="TrendingUp" size={24} color="var(--color-primary)" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold font-headline text-foreground">98%</p>
                    <p className="text-sm text-muted-foreground font-body">Client Satisfaction</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div>
          <div className="text-center mb-12 md:mb-16">
            <h3 className="text-2xl md:text-3xl lg:text-4xl font-bold font-headline text-foreground mb-4">
              Our Journey
            </h3>
            <p className="text-base md:text-lg text-muted-foreground font-body max-w-2xl mx-auto">
              Key milestones that shaped Devontis Technologies into the trusted partner we are today
            </p>
          </div>
          
          <div className="relative">
            <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-border transform md:-translate-x-1/2"></div>
            
            <div className="space-y-8 md:space-y-12">
              {milestones?.map((milestone, index) =>
              <div
                key={index}
                className={`relative flex flex-col md:flex-row items-start md:items-center ${
                index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`
                }>

                  <div className={`w-full md:w-5/12 ${index % 2 === 0 ? 'md:text-right md:pr-8' : 'md:text-left md:pl-8'}`}>
                    <div className="bg-card p-4 md:p-6 rounded-xl shadow-md border border-border ml-12 md:ml-0">
                      <div className="flex items-center space-x-3 mb-3">
                        <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                          <Icon name={milestone?.icon} size={20} color="var(--color-primary)" />
                        </div>
                        <span className="text-xl md:text-2xl font-bold font-headline text-primary">{milestone?.year}</span>
                      </div>
                      <h4 className="text-lg md:text-xl font-semibold font-headline text-foreground mb-2">
                        {milestone?.title}
                      </h4>
                      <p className="text-sm md:text-base text-muted-foreground font-body">
                        {milestone?.description}
                      </p>
                    </div>
                  </div>
                  
                  <div className="absolute left-4 md:left-1/2 w-8 h-8 bg-primary rounded-full border-4 border-background transform md:-translate-x-1/2 flex items-center justify-center">
                    <div className="w-3 h-3 bg-background rounded-full"></div>
                  </div>
                  
                  <div className="hidden md:block w-5/12"></div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>);

};

export default FounderSection;